import { useState, type MouseEvent as ReactMouseEvent } from 'react'
import { ChevronDown, ChevronRight, ExternalLink, Trash2 } from 'lucide-react'
import { aufCent } from '@symphenia/calc-core'
import { geldText } from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { Feld, ZeilenBlock, zelle } from '@/components/zeilenblock'
import { GeldFeldFuer, geldEingabe } from '@/components/geldfeld'
import { eingabeOffen, mitStand, useFeld, useGruppe, useSpeicherstelle, useStellenDaten, zahlText, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import type { CalcPosition, CalcPositionState, CalcUnit } from '../types/calculation.types'
import { DateiMarke } from './KalkulationsDateien'

export const ZUSTAND_ANZEIGE: Record<CalcPositionState, { label: string; klasse: string }> = {
  OPEN: { label: 'offen', klasse: 'bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300' },
  PRICED: { label: 'ausgepreist', klasse: 'bg-green-100 text-green-900 dark:bg-green-500/20 dark:text-green-300' },
  MANUAL: { label: 'von Hand', klasse: 'bg-sky-500/15 text-sky-800 dark:text-sky-300' },
  GAP_OPEN: { label: 'Lücke offen', klasse: 'bg-red-100 text-red-900 dark:bg-red-500/20 dark:text-red-300' },
}
/** Detailblatt K 3.4 — statt des Zustands: ohne Preis abgegeben, zählt nicht. */
export const NICHT_ANGEBOTEN = { label: 'nicht angeboten', klasse: 'bg-slate-200 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300' }


/** Schnellspalten und EP gehen über `…/quick`, alles Übrige über die Position (4.8). */
const SCHNELL = ['hoursPerUnit', 'laborPerUnit', 'materialPerUnit'] as const

// KA-49: keine vorgeschlagene Menge — leer gilt 1 (Platzhalter).
const NEU = { shortText: '', quantity: null, unitId: null } as unknown as Datensatz

/**
 * AP-37 4.8 — **eine Zeile des Positionsrasters**: Nr./OZ · Menge · Einheit ·
 * Bezeichnung · Std/E · Mat/E · EP · GP · Zustand (Reihenfolge wie im
 * Projektmanagement). Die Zeile ist eine Gruppe des
 * Speicherbausteins (AP-36): Die geänderten Felder gehen beim Verlassen der Zeile
 * hinaus; Std/E und Mat/E über die Schnellspalten, der Rest an die Position.
 *
 * **Was EP und GP bewegt, speichert je Feld** — Std/E und Lohn/E seit N1-28, Menge und
 * EP von Hand seit dem 7.10.2026 (Auftraggeber): EP und GP rechnet der Server aus dem
 * ganzen Blatt, und sie sollen dastehen, sobald das Feld verlassen ist, nicht erst nach
 * der ganzen Zeile.
 *
 * Eine gesperrte Schnellspalte (mehr als ein Eintrag im Blatt) zeigt die Summe
 * je Einheit kursiv. An einer LV-Position sind Text, Menge und Einheit gesperrt.
 * Ohne `p` ist es die leere letzte Zeile: Sie legt beim Verlassen an.
 */
export function PositionsZeile({
  kalkulationId,
  p,
  einheiten,
  darf,
  kosten,
  markiert,
  gezogen,
  ziellinie,
  autoFokus,
  onRand,
  onGeaendert,
  onAnlegen,
  onOeffnen,
  onLoeschen,
  texte,
  onTexte,
}: {
  kalkulationId: string
  p?: CalcPosition
  einheiten: CalcUnit[]
  darf: boolean
  kosten: boolean
  markiert?: boolean
  gezogen?: boolean
  ziellinie?: 'oben' | 'unten' | null
  autoFokus?: boolean
  onRand?: (e: ReactMouseEvent) => void
  onGeaendert: () => void
  onAnlegen?: (dto: { shortText: string; quantity: number; unitId?: string }, perTastatur: boolean) => Promise<void>
  onOeffnen?: () => void
  onLoeschen?: () => void
  /** N1-22: Einleitungs- und Langtext unter der Zeile zeigen. */
  texte?: boolean
  /** N1-24: die Texte dieser einen Position auf- oder zuklappen. */
  onTexte?: () => void
}) {
  const [fehler, setFehler] = useState<string | null>(null)
  const url = p ? `/calculations/${kalkulationId}/positions/${p.id}` : ''

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: p ? `calcPosition:${p.id}` : `calcPosition:neu:${kalkulationId}`,
    bezeichnung: p ? `Position ${p.displayNumber}` : 'neue Position',
    daten: (p ?? NEU) as unknown as Datensatz,
    schreibe: p
      ? async (patch, stand) => {
          const schnell: Datensatz = {}
          const rest: Datensatz = {}
          for (const [k, v] of Object.entries(patch)) ((SCHNELL as readonly string[]).includes(k) ? schnell : rest)[k] = v
          let antwort: Datensatz | undefined
          let s = stand
          if (Object.keys(rest).length) {
            antwort = (await api.patch<Datensatz>(url, rest, mitStand(s))).data
            s = ((antwort.data as Datensatz | undefined)?.updatedAt as string | undefined) ?? s
          }
          if (Object.keys(schnell).length) antwort = (await api.patch<Datensatz>(`${url}/quick`, schnell, mitStand(s))).data
          onGeaendert()
          return antwort!
        }
      : () => Promise.reject(new Error('Die leere Zeile wird angelegt, nicht geändert.')),
    keepalive: p ? { url, method: 'PATCH' } : undefined,
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: p?.shortText, gesperrt: !p })
  const d = useStellenDaten(stelle) as unknown as CalcPosition

  const lv = Boolean(p?.isLvLocked)
  const sperre = !darf
  // In der leeren Zeile gehören auch Menge und EP zur Gruppe: Dort wird angelegt, nicht gespeichert.
  const jeFeld = p ? {} : { gruppe }
  const text = useFeld<string>(stelle, 'shortText', { gruppe, label: 'Bezeichnung', pflicht: Boolean(p), gesperrt: sperre || lv })
  const menge = useFeld<number | null>(stelle, 'quantity', { ...jeFeld, art: 'zahl', label: 'Menge', stellen: 3, gesperrt: sperre || lv || d.kind === 'TEXT' })
  const einheit = useFeld<string | null>(stelle, 'unitId', { gruppe, art: 'auswahl', label: 'Einheit', gesperrt: sperre || lv })
  // N1-28: Std/E und Lohn/E speichern je Feld (nicht mit der Zeile) — so steht der umgerechnete Wert gleich daneben.
  const std = useFeld<number | null>(stelle, 'hoursPerUnit', { art: 'zahl', label: 'Std/E', stellen: 3, gesperrt: sperre || !p || !kosten || !d.hoursPerUnitEditable })
  // N1-27: mit Kostenrecht Lohn/E in Euro (Einzelkosten), der Server rechnet über den Kostensatz in Stunden um.
  const lohn = useFeld<number | null>(stelle, 'laborPerUnit', { art: 'zahl', schreib: geldEingabe, label: 'Lohn/E', gesperrt: sperre || !p || !kosten || !d.laborPerUnitEditable })
  const mat = useFeld<number | null>(stelle, 'materialPerUnit', { gruppe, art: 'zahl', schreib: geldEingabe, label: 'Mat/E', gesperrt: sperre || !p || !kosten || !d.materialPerUnitEditable })
  const ep = useFeld<number | null>(stelle, 'fixedUnitPrice', { ...jeFeld, art: 'zahl', schreib: geldEingabe, label: 'EP von Hand', gesperrt: sperre || !p || d.kind === 'TEXT' })
  // Mit einem EP von Hand ist der GP schlicht EP × Menge (`calc-core`, Schritt 4) — er steht schon beim Tippen da.
  // Ohne ihn hängt der EP am ganzen Blatt: Dann rechnet der Server, sobald das Feld verlassen ist.
  const gp = p && !p.notOffered && ep.wert != null && eingabeOffen(menge, ep) ? aufCent(aufCent(ep.wert) * (menge.wert ?? 0)) : p?.totalPrice

  async function anlegen(perTastatur: boolean) {
    const kurz = (text.wert ?? '').trim()
    if (!onAnlegen || !kurz) return
    try {
      setFehler(null)
      await onAnlegen({ shortText: kurz, quantity: menge.wert ?? 1, ...(einheit.wert ? { unitId: einheit.wert } : {}) }, perTastatur)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Position ließ sich nicht anlegen.'))
    }
  }

  const kursiv = (wert: string) => <span className="block px-1.5 py-1 text-right text-sm italic text-muted-foreground" title="Mehrere Einträge — im Blatt bearbeiten">{wert}</span>
  const ohnePreis = d.kind === 'TEXT'
  const zustand = p ? (p.notOffered ? NICHT_ANGEBOTEN : ZUSTAND_ANZEIGE[p.state]) : null

  return (
    <ZeilenBlock
      neu={!p}
      autoFokus={autoFokus}
      schluessel={p?.id}
      gezogen={gezogen}
      ziellinie={ziellinie}
      ziehbar={Boolean(p && darf && !lv)}
      markiert={markiert}
      onRand={p ? onRand : undefined}
      randLabel={p?.shortText}
      gruppe={p ? gruppe : undefined}
      onVerlassen={(perTastatur) => {
        if (!p) void anlegen(perTastatur)
      }}
      onVerwerfen={() => {
        gruppe.verwerfen()
        setFehler(null)
      }}
      // Menge und EP speichern je Feld — ihr Fehler steht sonst nirgends, die Gruppe zeigt nur den eigenen.
      fehler={fehler ?? (p ? (menge.fehler ?? ep.fehler) : null)}
      gedimmt={p ? ['OPTIONAL', 'ALTERNATIVE'].includes(p.kind) : false}
      detail={p && texte ? <PositionsTexte p={p} /> : undefined}
    >
      <Feld spalte="nr">
        <span className="flex items-center truncate px-1 py-1 text-sm tabular-nums text-muted-foreground" title={p?.kind !== 'NORMAL' ? p?.kind : undefined}>
          {p && onTexte && (p.introText || p.longText || p.gapQuestion) ? (
            <button type="button" className="-ml-1 mr-0.5 shrink-0 rounded hover:text-foreground" aria-label={texte ? 'Texte zuklappen' : 'Texte aufklappen'} aria-expanded={Boolean(texte)} onClick={onTexte}>
              {texte ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            p && onTexte && <span className="mr-0.5 inline-block w-4 shrink-0" aria-hidden />
          )}
          {p ? p.displayNumber : '+'}
          {p?.kind === 'OPTIONAL' && <span className="ml-1 text-[10px]">E</span>}
          {p?.kind === 'ALTERNATIVE' && <span className="ml-1 text-[10px]">W</span>}
        </span>
      </Feld>
      <Feld spalte="menge">
        <input data-start className={`${zelle} text-right`} inputMode="decimal" data-spalte="menge" placeholder={p ? undefined : '1'} {...menge.props} />
      </Feld>
      <Feld spalte="einheit">
        {p?.unitRaw && !p.unitId ? (
          <span className="block px-1.5 py-1 text-sm text-amber-700 dark:text-amber-400" title="Einheit aus dem LV ohne Übersetzung">
            {p.unitRaw}
          </span>
        ) : (
          <select className={zelle} data-spalte="einheit" {...einheit.props}>
            <option value="">—</option>
            {einheiten.map((e) => (
              <option key={e.id} value={e.id}>
                {e.code}
              </option>
            ))}
          </select>
        )}
      </Feld>
      <Feld spalte="kurztext">
        <input className={zelle} data-spalte="kurztext" placeholder={p ? undefined : 'Bezeichnung der neuen Position …'} title={p?.shortText} {...text.props} />
      </Feld>
      <Feld spalte="std">
        {p && !d.hoursPerUnitEditable ? kursiv(zahlText(d.hoursPerUnit, 2)) : <input className={`${zelle} text-right`} inputMode="decimal" data-spalte="std" {...std.props} />}
      </Feld>
      {kosten && (
        <Feld spalte="lohn">
          {p && !d.laborPerUnitEditable ? (
            kursiv(geldText(d.laborPerUnit ?? 0))
          ) : (
            <GeldFeldFuer className={zelle} data-spalte="lohn" title="Lohnkosten je Einheit in Euro — über den Kostensatz zu Std/E" feld={lohn.props} />
          )}
        </Feld>
      )}
      {kosten && (
        <Feld spalte="mat">
          {p && !d.materialPerUnitEditable ? kursiv(geldText(d.materialPerUnit ?? 0)) : <GeldFeldFuer className={zelle} data-spalte="mat" feld={mat.props} />}
        </Feld>
      )}
      <Feld spalte="ep">
        {ohnePreis ? (
          <span />
        ) : (
          <GeldFeldFuer
            className={`${zelle} ${p?.fixedUnitPrice == null ? 'placeholder:text-foreground' : 'font-medium text-sky-800 dark:text-sky-300'}`}
            data-spalte="ep"
            placeholder={p ? geldText(p.unitPrice) : ''}
            title={p?.fixedUnitPrice != null ? `EP von Hand — kalkuliert ${p.quantity > 0 ? geldText(p.priceCalculated / p.quantity) : '—'}` : 'EP kalkuliert — tippen setzt den EP von Hand'}
            feld={ep.props}
          />
        )}
      </Feld>
      <Feld spalte="gp">
        <span className={`block px-1.5 py-1 text-right text-sm tabular-nums ${p && ['OPTIONAL', 'ALTERNATIVE'].includes(p.kind) ? 'text-muted-foreground line-through decoration-muted-foreground/40' : ''}`}>
          {p && !ohnePreis ? geldText(gp) : ''}
        </span>
      </Feld>
      <Feld spalte="zustand">
        {/* Eine Textposition hat keinen Preis, also auch keinen Zustand. */}
        {zustand && !ohnePreis && (
          <span className="flex flex-wrap items-center gap-1">
            <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${zustand.klasse}`}>{zustand.label}</span>
            {p?.rateMissing && (
              <span className="text-[11px] text-destructive" title="Ein Arbeitsschritt hat keinen Satz — Lohn 0">
                Satz fehlt
              </span>
            )}
            {p?.hasSurchargeOverride && (
              <span className="text-[11px] text-muted-foreground" title="Abweichende Aufschläge an dieser Position">
                %
              </span>
            )}
            {p && <DateiMarke gesamt={p.fileCount} anZeilen={p.fileCountLines} />}
          </span>
        )}
        {/* Eine Textposition kann trotzdem Unterlagen tragen. */}
        {ohnePreis && p && <DateiMarke gesamt={p.fileCount} anZeilen={p.fileCountLines} />}
      </Feld>
      <Feld spalte="aktion">
        {p && (
          <span className="flex items-center justify-end gap-1.5">
            <button type="button" className="text-muted-foreground hover:text-primary" title="Position öffnen (Blatt)" onClick={onOeffnen}>
              <ExternalLink className="h-4 w-4" />
            </button>
            {darf && !lv && onLoeschen && (
              <button type="button" className="text-muted-foreground hover:text-destructive" title="In den Papierkorb" onClick={onLoeschen}>
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </span>
        )}
      </Feld>
    </ZeilenBlock>
  )
}

/** N1-22: die Texte einer Position wie im gedruckten LV — Einleitung, Langtext, Bieterlücke. */
function PositionsTexte({ p }: { p: CalcPosition }) {
  if (!p.introText && !p.longText && !p.gapQuestion) return null
  return (
    <div className="max-w-5xl space-y-1.5 pb-2 pl-10 pr-3 pt-0.5 text-sm leading-relaxed text-foreground/80">
      {p.introText && <p className="whitespace-pre-line italic text-muted-foreground">{p.introText}</p>}
      {p.longText && <p className="whitespace-pre-line">{p.longText}</p>}
      {p.gapQuestion && (
        <p className="rounded-md border border-dashed border-amber-500/50 px-2 py-1">
          <span className="text-muted-foreground">{p.gapQuestion}</span> <span className="font-medium">{p.gapAnswer || '— noch offen —'}</span>
        </p>
      )}
    </div>
  )
}
